import React from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Progress } from "../ui/progress";
import { Badge } from "../ui/badge";
import { CheckCircle2, Circle, Trophy, Flame, BookOpen, Brain, Target, Coins } from "lucide-react";
import { toast } from "sonner";
import { useGamification } from "../../contexts/GamificationContext";
import { useLanguage } from "../../App";

const ChallengeCard = ({ challenge, onComplete, loading = false }) => {
  const { language } = useLanguage();
  const isEnglish = language === "en";
  const { completeChallenge } = useGamification();

  const {
    title,
    title_id,
    description,
    description_id,
    target,
    user_progress = 0,
    completed = false,
    xp_reward,
    category,
    challenge_index
  } = challenge;

  const progress = Math.min((user_progress / target) * 100, 100);

  const categoryIcons = {
    shalat: Flame,
    quran: BookOpen,
    general: Target,
    infaq: Coins,
    hafalan: Brain
  };

  const categoryColors = {
    shalat: "bg-purple-100 text-purple-700 border-purple-300",
    quran: "bg-teal-100 text-teal-700 border-teal-300",
    general: "bg-blue-100 text-blue-700 border-blue-300",
    infaq: "bg-amber-100 text-amber-700 border-amber-300",
    hafalan: "bg-pink-100 text-pink-700 border-pink-300"
  };

  const Icon = categoryIcons[category] || Target;

  const handleComplete = async () => {
    if (completed) return;

    try {
      const result = await completeChallenge(challenge_index, target);
      if (result && result.completed) {
        toast.success(
          isEnglish
            ? `Challenge completed! +${result.xp_earned} XP`
            : `Tantangan selesai! +${result.xp_earned} XP`
        );
      }
    } catch (error) {
      console.error("Error completing challenge:", error);
      toast.error(isEnglish ? "Failed to complete challenge" : "Gagal menyelesaikan tantangan");
    }
  };

  return (
    <Card className={`transition-all ${completed ? "bg-green-50 border-green-300" : ""}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div
            className={`
              w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0
              ${categoryColors[category]}
            `}
          >
            {completed ? (
              <CheckCircle2 className="w-6 h-6 text-green-600" />
            ) : (
              <Icon className="w-6 h-6" />
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-1">
              <div>
                <h4 className="font-semibold text-sm">{isEnglish ? title : title_id}</h4>
                <p className="text-xs text-muted-foreground">
                  {isEnglish ? description : description_id}
                </p>
              </div>
              <Badge variant="secondary" className="flex-shrink-0">
                <Trophy className="w-3 h-3 mr-1" />
                +{xp_reward}
              </Badge>
            </div>

            {/* Progress */}
            <div className="space-y-2 mt-3">
              <Progress value={progress} className="h-2" />
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>
                  {user_progress} / {target} {isEnglish ? "completed" : "selesai"}
                </span>
                <span>{Math.round(progress)}%</span>
              </div>
            </div>

            {/* Action button */}
            {user_progress >= target && !completed && (
              <Button
                size="sm"
                className="w-full mt-3"
                onClick={handleComplete}
                disabled={loading}
              >
                {loading
                  ? (isEnglish ? "Claiming..." : "Mengambil...")
                  : (isEnglish ? "Claim Reward" : "Ambil Hadiah")
                }
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ChallengeCard;
